// ProjectTreeHeader — the "PROJECTS" eyebrow + inline + action that lives
// at the very top of the left panel. Modelled on the Pre-Sales Visualizer's
// ENGAGEMENTS panel (see reference 2026-05-21_21h54_19.png): tiny uppercase
// label flush left, single circular `+` button flush right.
//
// Why a dedicated component:
//   • Lets the parent ProjectTree stay focussed on tree state + dnd wiring.
//   • The eyebrow is reused below the tree (sticky bottom "New folder" CTA)
//     so isolating the markup makes restyling a one-file change.
//
// Hard rules respected:
//   • All colors via --ws-* tokens (no raw hex).
//   • Lucide icons only.
//   • No new deps.

import { ChevronsDownUp, ChevronsUpDown, Hash, Plus, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ProjectTreeHeaderProps {
  /** Section label — usually "PROJECTS" but kept overridable for reuse. */
  label?: string;
  /** Fires when the user clicks the trailing + button. */
  onAdd: () => void;
  /** Optional secondary action — when present we render a refresh icon button
   *  to the LEFT of the + so the spatial ordering matches user expectation
   *  (destructive-ish actions on the right, neutral ones on the left). */
  onRefresh?: () => void;
  /** Expand-all ⇄ collapse-all. Icon-only; the tooltip carries the meaning —
   *  the header has no room for a caption and the glyph flips to show state. */
  onToggleExpandAll?: () => void;
  /** Drives which glyph + tooltip the toggle shows. */
  allExpanded?: boolean;
  /** Row-numbering on/off — beside Expand-all, same icon-only + tooltip
   *  convention. Lets a workspace that already numbers its own filenames
   *  ("01 Something") turn off the computed gutter number. */
  onToggleNumbering?: () => void;
  /** Drives which glyph + tooltip the numbering toggle shows. */
  numberingOn?: boolean;
}

export function ProjectTreeHeader({
  label,
  onAdd,
  onRefresh,
  onToggleExpandAll,
  allExpanded = false,
  onToggleNumbering,
  numberingOn = true,
}: ProjectTreeHeaderProps) {
  const { t } = useTranslation('projects');
  const resolvedLabel = label ?? t('tree.header.defaultLabel');
  // Mobile (< lg — the drawer / touch aside): 24px icon buttons are too small
  // to tap reliably, so they grow to 36px there. Desktop keeps the tight 24px.
  const iconBtn = 'w-6 h-6 max-lg:w-9 max-lg:h-9 inline-flex items-center justify-center rounded-full hover:text-ws-ink hover:bg-ws-surface-2';
  return (
    <div className="flex items-center justify-between px-3 pt-3 pb-2 flex-shrink-0">
      <p className="ws-eyebrow">{resolvedLabel}</p>
      <div className="flex items-center gap-0.5">
        {onToggleExpandAll && (
          <button
            type="button"
            aria-label={allExpanded ? t('tree.header.collapseAllAria') : t('tree.header.expandAllAria')}
            aria-pressed={allExpanded}
            title={allExpanded ? t('tree.header.collapseAllTitle') : t('tree.header.expandAllTitle')}
            className={`${iconBtn} text-ws-ink-3`}
            onClick={onToggleExpandAll}
          >
            {allExpanded
              ? <ChevronsDownUp size={12} strokeWidth={1.75} />
              : <ChevronsUpDown size={12} strokeWidth={1.75} />}
          </button>
        )}
        {onToggleNumbering && (
          <button
            type="button"
            aria-label={numberingOn ? t('tree.header.hideNumberingAria') : t('tree.header.showNumberingAria')}
            aria-pressed={numberingOn}
            title={numberingOn ? t('tree.header.hideNumberingTitle') : t('tree.header.showNumberingTitle')}
            className={`${iconBtn} ${numberingOn ? 'text-ws-ink' : 'text-ws-ink-3'}`}
            onClick={onToggleNumbering}
          >
            <Hash size={12} strokeWidth={1.75} />
          </button>
        )}
        {onRefresh && (
          <button
            type="button"
            aria-label={t('tree.header.refreshAria', { label: resolvedLabel.toLowerCase() })}
            title={t('tree.header.refreshTitle')}
            className={`${iconBtn} text-ws-ink-3`}
            onClick={onRefresh}
          >
            <RefreshCw size={12} strokeWidth={1.75} />
          </button>
        )}
        <button
          type="button"
          aria-label={t('tree.header.newAria', { label: resolvedLabel.toLowerCase().replace(/s$/, '') })}
          title={t('tree.header.newFolderTitle')}
          className={`${iconBtn} text-ws-ink-2`}
          onClick={onAdd}
        >
          <Plus size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
